import type { QueryClient } from '@tanstack/react-query';

export const PORTAL_SESSION_EVENT_KEY = 'helios.portal.session-change';
const OWN_SESSION_EVENT = 'helios:portal-session-change';
const EXPIRED_SESSION_EVENT = 'helios:portal-session-expired';
let sessionGeneration = 0;

export function portalSessionGeneration(): number {
  return sessionGeneration;
}

export function advancePortalSessionGeneration(): void {
  sessionGeneration += 1;
}

/** Cancel before removal, including inactive photo/document and finance keys. */
export async function clearPortalQueries(client: QueryClient): Promise<void> {
  const cancelled = client.cancelQueries({ queryKey: ['portal'] });
  client.removeQueries({ queryKey: ['portal'] });
  await cancelled;
}

/** A nonce only: identities, session tokens and CSRF values never leave the tab. */
function notifyPeerTabs(): void {
  try {
    window.localStorage.setItem(
      PORTAL_SESSION_EVENT_KEY,
      `v1:${Date.now()}:${Math.random().toString(36).slice(2)}`,
    );
  } catch {
    // The current tab still clears its cache when browser storage is unavailable.
  }
}

/** A successful verification/logout acknowledges the initiating tab's own change. */
export function publishPortalSessionChange(): void {
  window.dispatchEvent(new Event(OWN_SESSION_EVENT));
  notifyPeerTabs();
}

/** Authenticated expiry must invalidate the initiating tab as well as its peers. */
export function expirePortalSession(): void {
  advancePortalSessionGeneration();
  window.dispatchEvent(new Event(EXPIRED_SESSION_EVENT));
  notifyPeerTabs();
}

export function subscribePortalSessionChanges(
  onPeerChange: () => void,
  onOwnChange: () => void,
): () => void {
  const onStorage = (event: StorageEvent) => {
    if (
      event.key === PORTAL_SESSION_EVENT_KEY &&
      typeof event.newValue === 'string' &&
      /^v1:\d+:[a-z0-9]+$/.test(event.newValue)
    ) {
      advancePortalSessionGeneration();
      onPeerChange();
    }
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(OWN_SESSION_EVENT, onOwnChange);
  window.addEventListener(EXPIRED_SESSION_EVENT, onPeerChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(OWN_SESSION_EVENT, onOwnChange);
    window.removeEventListener(EXPIRED_SESSION_EVENT, onPeerChange);
  };
}
